const measureAsync = (telemetry, name, operation, getDetails) => {
  let span = null;
  try {
    span = telemetry?.startSpan(name) || null;
  } catch {
    // Diagnostics must never block local persistence or synchronization.
  }

  return Promise.resolve()
    .then(operation)
    .then((result) => {
      try {
        telemetry?.endSpan(span, getDetails(result));
      } catch {
        // Preserve the functional result when diagnostics fail.
      }
      return result;
    })
    .catch((error) => {
      try {
        telemetry?.endSpan(span, {});
      } catch {
        // Preserve the original operation error.
      }
      throw error;
    });
};

/**
 * Decorates synchronization reads with optional performance diagnostics.
 * The underlying coordinator remains a functional persistence collaborator and
 * does not know about telemetry or browser timing APIs.
 *
 * @param {Object} coordinator - Functional sync coordinator.
 * @param {Object|null} telemetry - Optional performance telemetry port.
 * @returns {Object} Coordinator-compatible measured decorator.
 */
export const createMeasuredSyncCoordinator = (coordinator, telemetry) => ({
  ...coordinator,
  readLocalEntities: (entityType) =>
    measureAsync(
      telemetry,
      'local-read',
      () => coordinator.readLocalEntities(entityType),
      (entities) => ({ entityType, count: entities.length })
    ),
  readLocalEntitiesByIds: (entityType, ids) =>
    measureAsync(
      telemetry,
      'local-read-by-ids',
      () => coordinator.readLocalEntitiesByIds(entityType, ids),
      (entities) => ({
        entityType,
        requestedCount: ids.length,
        count: entities.length
      })
    )
});
